// ====================================================================================
// COMPONENT: ArchetypeSynthesis (Fixed: Anchored Height & Internal Scroll)
// Self-contained with InsightCard, chart, archetype list, and all icons.
// ====================================================================================
import {
  Users,
  BarChart3,
  PieChart,
  Star,
  TrendingUp as TrendingUpIcon,
} from "lucide-react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

// --- Internal Color Palette ---
const CHART_COLORS = [
  '#3b82f6',
  '#22c55e',
  '#facc15',
  '#ef4444',
  '#6366f1',
  '#f97316',
  '#64748b',
];

// --- Main Component ---
const ArchetypeSynthesis = ({ archetypeData, setDrawerData }) => {

  const getTopIssues = (issues) => {
    if (!issues) return [];
    return Object.entries(issues)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([key]) => key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '));
  };

  // --- Strategic Synthesis Logic ---
  let insights = { largest: null, mostPersuadable: null, highestTurnout: null };
  let chartData = [];

  if (archetypeData && archetypeData.length > 0) {
    const sortedByPop = [...archetypeData].sort((a, b) => b.estimated_population - a.estimated_population);

    insights.largest = sortedByPop[0];
    insights.mostPersuadable = [...archetypeData].sort((a, b) => b.avg_persuadability_score - a.avg_persuadability_score)[0];
    insights.highestTurnout = [...archetypeData].sort((a, b) => b.avg_turnout_probability - a.avg_turnout_probability)[0];

    const topN = 5;
    chartData = sortedByPop.slice(0, topN).map(a => ({ name: a.name, value: a.estimated_population }));
    const otherPop = sortedByPop.slice(topN).reduce((acc, a) => acc + a.estimated_population, 0);
    if (otherPop > 0) {
      chartData.push({ name: 'Other', value: otherPop });
    }
  }

  const totalPopulation = archetypeData?.reduce((sum, a) => sum + a.estimated_population, 0) || 0;

  // Helper for Insight Cards
  const InsightCard = ({ icon: Icon, label, value, subValue, subLabel, colorClass, borderClass }) => (
    <div className={`p-3 rounded-lg border ${borderClass} bg-slate-950/30 flex flex-col justify-between group hover:bg-slate-900/50 transition-colors shadow-sm`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={14} className={colorClass} />
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</span>
        </div>
        <span className={`text-xs font-mono font-bold ${colorClass}`}>{value}</span>
      </div>
      <div>
        <div className="text-sm font-bold text-white leading-tight mb-1 truncate group-hover:text-slate-200 transition-colors">{subValue}</div>
        <div className="text-[10px] text-slate-500 truncate">{subLabel}</div>
      </div>
    </div>
  );

  return (
    // ROOT: Fixed Height to match the left column (Swing + Candidate)
    <div className="bg-slate-900/40 border border-slate-700/50 rounded-xl flex flex-col h-[1270px] overflow-hidden">

      {/* HEADER */}
      <div className="px-5 py-4 border-b border-slate-800/50 flex items-center justify-between bg-slate-900/20 shrink-0">
        <div className="flex items-center gap-2.5">
          <Users size={16} className="text-indigo-400" />
          <div className="flex flex-col">
            <h3 className="text-[10px] font-bold text-slate-200 uppercase tracking-widest">Voter Segmentation</h3>
            <p className="text-[9px] text-slate-500 font-medium leading-none mt-0.5">
              {totalPopulation.toLocaleString()} total voters
            </p>
          </div>
        </div>
        <div className="px-2 py-1 rounded bg-slate-900 border border-slate-800 text-[10px] font-mono text-slate-400">
          {archetypeData?.length || 0} SEGS
        </div>
      </div>

      {/* CONTENT BODY */}
      <div className="flex-1 flex flex-col min-h-0 p-5 gap-5 overflow-hidden">

        {/* 1. INSIGHTS: Fixed Block */}
        <div className="grid grid-cols-1 gap-3 shrink-0">
          <InsightCard
            icon={PieChart}
            label="Largest Segment"
            value={`${insights.largest?.percentage_of_total_campaign_pop.toFixed(1) || 0}%`}
            subValue={insights.largest?.name || 'N/A'}
            subLabel={`${insights.largest?.estimated_population.toLocaleString() || 0} voters`}
            colorClass="text-accent-400"
            borderClass="border-accent-500/20"
          />
          <InsightCard
            icon={Star}
            label="Highest Potential"
            value={`${(insights.mostPersuadable?.avg_persuadability_score * 100 || 0).toFixed(0)}%`}
            subValue={insights.mostPersuadable?.name || 'N/A'}
            subLabel="Priority targeting recommended"
            colorClass="text-emerald-400"
            borderClass="border-emerald-500/20"
          />
          <InsightCard
            icon={TrendingUpIcon}
            label="Mobilization Target"
            value={`${(insights.highestTurnout?.avg_turnout_probability * 100 || 0).toFixed(0)}%`}
            subValue={insights.highestTurnout?.name || 'N/A'}
            subLabel="GOTV optimization focus"
            colorClass="text-amber-400"
            borderClass="border-amber-500/20"
          />
        </div>

        {/* 2. CHART: Fixed Block */}
        <div className="bg-slate-950/30 rounded-lg p-4 border border-slate-800/50 flex-none h-[220px] flex flex-col shrink-0">
          <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
            <BarChart3 size={12} /> Population Distribution
          </h3>

          <div className="flex-grow relative w-full min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="60%"
                  outerRadius="80%"
                  fill="#8884d8"
                  paddingAngle={3}
                  labelLine={false}
                  className="cursor-pointer focus:outline-none"
                  onClick={(data) => {
                    if (data.name === 'Other') return;
                    const archetype = archetypeData.find(a => a.name === data.name);
                    if (archetype) {
                      setDrawerData({
                        title: archetype.name,
                        component: 'ArchetypeDetailDrawer',
                        data: archetype,
                      });
                    }
                  }}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                      stroke="rgba(15, 23, 42, 0.5)"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(15, 23, 42, 0.95)',
                    borderColor: 'rgba(51, 65, 85, 0.5)',
                    borderRadius: '0.5rem',
                    fontSize: '11px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                  itemStyle={{ color: '#f1f5f9' }}
                  formatter={(value) => value.toLocaleString()}
                />
              </RechartsPieChart>
            </ResponsiveContainer>

            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center opacity-50">
                <Users size={16} className="mx-auto mb-1 text-slate-400" />
              </div>
            </div>
          </div>
        </div>

        {/* 3. SCROLLABLE LIST */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest pl-1 mb-3 shrink-0">
            Detailed Breakdown
          </h4>

          <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-3">
            {archetypeData?.map((archetype, index) => (
              <div
                key={archetype.name}
                onClick={() => setDrawerData({
                  title: archetype.name,
                  component: 'ArchetypeDetailDrawer',
                  data: archetype,
                })}
                className="relative bg-slate-900/40 p-3 rounded-r-lg border-y border-r border-slate-800/50 hover:bg-slate-800/60 hover:border-slate-700 transition-all duration-200 cursor-pointer group"
              >
                <div
                  className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg"
                  style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                />

                <div className="pl-2">
                  {/* Title Row */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="min-w-0 pr-2">
                      <h3 className="text-xs font-bold text-slate-200 group-hover:text-white transition-colors truncate">
                        {archetype.name}
                      </h3>
                      <div className="text-[10px] text-slate-500 mt-0.5 truncate">{archetype.main_segment}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs font-mono font-bold text-white">{archetype.estimated_population.toLocaleString()}</div>
                      <div className="text-[9px] text-slate-500">{archetype.percentage_of_total_campaign_pop.toFixed(1)}%</div>
                    </div>
                  </div>

                  {/* 3-Column Metrics Grid */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-slate-950/40 px-2 py-1.5 rounded border border-slate-800/50">
                      <div className="text-[9px] text-slate-500 uppercase tracking-wide mb-0.5">Persuade</div>
                      <div className="text-xs font-bold text-emerald-400">{(archetype.avg_persuadability_score * 100).toFixed(0)}%</div>
                    </div>
                    <div className="bg-slate-950/40 px-2 py-1.5 rounded border border-slate-800/50">
                      <div className="text-[9px] text-slate-500 uppercase tracking-wide mb-0.5">Turnout</div>
                      <div className="text-xs font-bold text-amber-400">{(archetype.avg_turnout_probability * 100).toFixed(0)}%</div>
                    </div>
                    <div className="bg-slate-950/40 px-2 py-1.5 rounded border border-slate-800/50">
                      <div className="text-[9px] text-slate-500 uppercase tracking-wide mb-0.5">Age</div>
                      <div className="text-xs font-bold text-white">{archetype.avg_age}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchetypeSynthesis;
